<template>
  <div>
    <v-data-table :headers="headers" :items="references" style="width: 100%;" :search="search" :loading="loading">
        <template v-slot:top>
          <v-toolbar flat color="white">
              <v-spacer></v-spacer>
              <v-text-field
              v-model="search"
              append-icon="mdi-magnify"
              :label="$t('components.ReferenceManager.search')"
              single-line
              hide-details
              ></v-text-field>
          </v-toolbar>
          </template>
          <template v-slot:item.users="{ item }">
          <v-btn icon @click="showDialog(item)">
              <v-icon small>mdi-account-supervisor</v-icon>
          </v-btn>
        </template>
    </v-data-table>
    <v-dialog v-model="dialog" fullscreen hide-overlay transition="dialog-bottom-transition" persistent>
      <v-card>
        <v-toolbar dark color="primary">
          <v-btn icon dark @click.stop="dialog = false; selectedReference = {}">
            <v-icon>mdi-close</v-icon>
          </v-btn>
          <v-toolbar-title>{{ $t('components.ReferenceManager.userAccessFor') }} {{ referenceType === 'ekrms' ? (selectedReference.definition && selectedReference.definition.reference ? selectedReference.definition.reference.category : '') : ('config' in selectedReference ? selectedReference.config.category : '') }}</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-toolbar-items>
            <v-btn dark text @click.stop="saveDialog = true">{{ $t('components.ReferenceManager.save') }}</v-btn>
            <v-dialog v-model="saveDialog" max-width="400">
              <v-card>
                <v-card-title class="headline">{{ $t('components.ReferenceManager.warning') }}</v-card-title>
                <v-card-text>
                  {{ $t('components.ReferenceManager.cannotBeUndone') }}
                </v-card-text>
                <v-card-actions v-if="!processing">
                  <v-spacer></v-spacer>
                  <v-btn color="gray darken-1" text @click="saveDialog = false; selectedUsersTable = []">{{ $t('components.ReferenceManager.abort') }}</v-btn>
                  <v-btn color="red darken-1" text @click="updateReferenceUsers()">{{ $t('components.ReferenceManager.confirm') }}</v-btn>
                </v-card-actions>
                <v-card-actions v-else>
                  <v-spacer></v-spacer>
                  {{ $t('components.ReferenceManager.processing') }}
                </v-card-actions>
              </v-card>
            </v-dialog>
          </v-toolbar-items>
        </v-toolbar>
        <v-row>
          <v-col cols="12">
            <v-data-table v-model="selectedUsersTable" :headers="usersTableHeaders" :items="managedUsers" class="elevation-1" style="width: 95%;" :loading="loading" show-select item-key="id" disable-pagination hide-default-footer :search="searchSelectedUsersTable">
              <template v-slot:top>
                <v-toolbar flat color="white">
                  <v-spacer></v-spacer>
                  <v-text-field
                    v-model="searchSelectedUsersTable"
                    append-icon="mdi-magnify"
                    :label="$t('components.ReferenceManager.search')"
                    single-line
                    hide-details
                  ></v-text-field>
                </v-toolbar>
              </template>
            </v-data-table>
          </v-col>
        </v-row>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { isComposerEkrm } from '@/utils/ekrmOrigin.js'
export default {
  data () {
    return {
      search: '',
      selectedUsers: {},
      searchSelectedUsersTable: '',
      selectedReference: {},
      selectedUsersTable: [],
      dialog: false,
      saveDialog: false,
      loading: true,
      processing: false,
      loadingTimeout: null
    }
  },
  methods: {
    ...mapActions('manager', [
      'loadReferenceModels',
      'loadEkrmModels',
      'batchSaveReferenceUserAccess'
    ]),
    // Extract initialization logic so it can be called from both created hook and watcher
    initializeSelectedUsers: function (users) {
      let referenceType = 'references'
      if (this.referenceType === 'ekrms') {
        referenceType = 'ekrms'
      }
      this.selectedUsers = {}
      if (users) {
        users.forEach(element => {
          if (element[referenceType]) {
            element[referenceType].forEach(reference => {
              if (!this.selectedUsers[reference]) {
                this.selectedUsers[reference] = []
              }
              if (!this.selectedUsers[reference].some(u => u.id === element.id)) {
                this.selectedUsers[reference].push(element)
              }
            })
          }
        })
      }
    },
    showDialog: function (item) {
      this.selectedReference = item
      this.dialog = true
      this.selectedUsersTable = this.selectedUsers[item.codename] || []
    },
    updateReferenceUsers: async function () {
      this.processing = true

      try {
        const referenceType = this.referenceType === 'ekrms' ? 'ekrms' : 'references'
        const selectedUserIds = this.selectedUsersTable.map(u => u.id)

        // Build userChanges array for batch processing
        const userChanges = []

        for (const user of this.managedUsers) {
          const userHasReference = user[referenceType].includes(this.selectedReference.codename)
          const userIsSelected = selectedUserIds.includes(user.id)

          let enabledReferences = []
          let disabledReferences = []

          // If user should have access but doesn't
          if (userIsSelected && !userHasReference) {
            enabledReferences.push(this.selectedReference.codename)
          } else if (!userIsSelected && userHasReference) {
            // If user shouldn't have access but does
            disabledReferences.push(this.selectedReference.codename)
          }

          // Only include users that have changes
          if (enabledReferences.length > 0 || disabledReferences.length > 0) {
            userChanges.push({
              userId: user.id,
              enabledReferences: enabledReferences,
              disabledReferences: disabledReferences
            })
          }
        }

        // Make single batch call to cloud function if there are changes
        if (userChanges.length > 0) {
          const result = await this.batchSaveReferenceUserAccess({
            userChanges: userChanges,
            referenceType: referenceType
          })

          if (result && result.success) {
            console.log(`Successfully updated ${referenceType} access:`, result.message)
          }
        }

        // Close dialogs (data will be updated automatically via Firestore listeners)
        this.saveDialog = false
        this.dialog = false
        this.selectedUsersTable = []
      } catch (error) {
        console.error('Error updating reference users:', error)
        // Could show error to user here
      } finally {
        this.processing = false
      }
    }
  },
  computed: {
    ...mapGetters('manager', [
      'managedUsers',
      'referenceModels',
      'ekrmModelsObj',
      'ekrmModels'
    ]),
    headers: function () {
      let headers = [
        { text: this.$t('components.ReferenceManager.name'), value: 'config.category' },
        { text: this.$t('components.ReferenceManager.users'), value: 'users', sortable: false }
      ]
      if (this.referenceType === 'ekrms') {
        headers = [
          { text: this.$t('components.ReferenceManager.name'), value: 'definition.reference.category' },
          { text: this.$t('components.ReferenceManager.users'), value: 'users', sortable: false }
        ]
      }
      return headers
    },
    usersTableHeaders: function () {
      return [{ text: this.$t('components.ReferenceManager.email'), value: 'email' }]
    },
    references: function () {
      if (this.referenceType === 'references') {
        return Object.keys(this.referenceModels).map(key => {
          return {
            ...this.referenceModels[key],
            codename: key
          }
        })
      } else {
        // The Model Composer's EKRMs are not assignable: their models carry their own permissions
        return Object.keys(this.ekrmModelsObj).filter(key => !isComposerEkrm(this.ekrmModelsObj[key])).map(key => {
          return {
            ...this.ekrmModelsObj[key],
            codename: key
          }
        })
      }
    }
  },
  watch: {
    'managedUsers': function (users) {
      // Re-initialize when managedUsers updates (e.g., after permissions load)
      this.initializeSelectedUsers(users)
    },
    'references': {
      handler: function (newReferences) {
        // When Firestore listener fires and populates data, stop loading
        // This handles both cases: data arrives OR empty collection (no data)
        if (newReferences !== undefined && newReferences !== null) {
          this.loading = false
          // Clear the timeout since data has arrived
          if (this.loadingTimeout) {
            clearTimeout(this.loadingTimeout)
            this.loadingTimeout = null
          }
        }
      },
      immediate: false
    }
  },
  props: {
    referenceType: {
      type: String,
      required: false,
      default: function () {
        return 'references'
      }
    }
  },
  created: async function () {
    // OPTIMIZATION: Only load the data type we actually need based on referenceType prop
    // Note: managedUsers are loaded by parent component (manageUserModels.vue)
    if (this.referenceType === 'ekrms') {
      await this.loadEkrmModels()
    } else {
      await this.loadReferenceModels()
    }

    // Initialize selectedUsers with current managedUsers data
    // (The watcher won't fire on creation, only on changes)
    this.initializeSelectedUsers(this.managedUsers)

    // Set a timeout fallback to stop loading after 3 seconds if data hasn't arrived
    // The watcher will turn off loading sooner when data actually arrives
    this.loadingTimeout = setTimeout(() => {
      this.loading = false
      this.loadingTimeout = null
    }, 3000)
  },
  beforeDestroy: function () {
    // Clean up timeout when component is destroyed
    if (this.loadingTimeout) {
      clearTimeout(this.loadingTimeout)
      this.loadingTimeout = null
    }
  }
}
</script>
